import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import "./i18n"; // Import i18n configuration
import { ThemeProvider } from "./contexts/ThemeContext";
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Register from "./pages/Register";
import MaintenancePage from "./pages/MaintenancePage";
import Dashboard from "./pages/Dashboard";
import ProjectDetail from "./pages/ProjectDetail";
import Settings from "./pages/Settings";
import Templates from "./pages/Templates";
import Reports from "./pages/Reports";
import SupervisorPanel from "./pages/SupervisorPanel";
import SupervisorSMTPSettings from "./pages/SupervisorSMTPSettings";
import ActivityLogs from "./pages/ActivityLogs";
import ActiveUsers from "./pages/ActiveUsers";
import UserManagement from "./pages/UserManagement";
import SMTPSettings from "./pages/SMTPSettings";
import Resources from "./pages/Resources";
import Sprints from "./pages/Sprints";
import ResourceSearch from "./pages/ResourceSearch";
import Timesheets from "./pages/Timesheets";
import HealthStatusPage from "./pages/HealthStatusPage";
import TaskAgingPage from "./pages/TaskAgingPage";
import ExportPage from "./pages/ExportPage";
import FeaturesDemo from "./pages/FeaturesDemo";
import { Toaster } from "./components/ui/sonner";

const normalizeUrl = (url) => {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  return trimmed.replace(/\/+$/, "");
};

const getBackendBaseUrl = () => {
  const envUrl = normalizeUrl(process.env.REACT_APP_BACKEND_URL);
  if (envUrl) {
    return envUrl;
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }

  // Fallback for non-browser environments (tests, SSR) when env var is missing
  return "http://localhost:8001";
};

const BACKEND_URL = getBackendBaseUrl();
export const API = `${BACKEND_URL}/api`;

export const axiosInstance = axios.create({
  baseURL: API,
  timeout: 30000, // 30 second timeout
});

// Add token to requests
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Add retry metadata
  config.metadata = { retryCount: 0 };
  return config;
});

// Response interceptor with retry logic
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // Check if maintenance mode (503 Service Unavailable)
    if (error.response?.status === 503) {
      // Redirect to maintenance page
      window.location.href = '/maintenance';
      return Promise.reject(error);
    }
    
    // Retry logic for network errors and 502/504 errors
    const shouldRetry = 
      !error.response || // Network error
      error.response.status === 502 || // Bad Gateway
      error.response.status === 504; // Gateway Timeout
    
    if (shouldRetry && config.metadata.retryCount < 3) {
      config.metadata.retryCount += 1;
      
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, config.metadata.retryCount - 1) * 1000;
      
      console.log(`Retrying request (${config.metadata.retryCount}/3) after ${delay}ms...`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
      
      return axiosInstance(config);
    }
    
    // If auth error, clear token and redirect to login
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

function PrivateRoute({ children }) {
  const token = localStorage.getItem("token");
  return token ? children : <Navigate to="/login" />;
}

function AdminRoute({ children }) {
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  
  if (!token) {
    return <Navigate to="/login" />;
  }
  
  if (user.role !== "admin" && user.role !== "supervisor") {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/maintenance" element={<MaintenancePage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/:projectId"
            element={
              <PrivateRoute>
                <ProjectDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <Settings />
              </PrivateRoute>
            }
          />
          <Route
            path="/templates"
            element={
              <PrivateRoute>
                <Templates />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <PrivateRoute>
                <Reports />
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/:projectId/resources"
            element={
              <PrivateRoute>
                <Resources />
              </PrivateRoute>
            }
          />
          <Route
            path="/sprints"
            element={
              <PrivateRoute>
                <Sprints />
              </PrivateRoute>
            }
          />
          <Route
            path="/resource-search"
            element={
              <PrivateRoute>
                <ResourceSearch />
              </PrivateRoute>
            }
          />
          <Route
            path="/timesheets"
            element={
              <PrivateRoute>
                <Timesheets />
              </PrivateRoute>
            }
          />
          <Route
            path="/health-status"
            element={
              <PrivateRoute>
                <HealthStatusPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/task-aging"
            element={
              <PrivateRoute>
                <TaskAgingPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/export"
            element={
              <PrivateRoute>
                <ExportPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/features-demo"
            element={
              <PrivateRoute>
                <FeaturesDemo />
              </PrivateRoute>
            }
          />
          <Route
            path="/supervisor"
            element={
              <PrivateRoute>
                <SupervisorPanel />
              </PrivateRoute>
            }
          />
          <Route
            path="/supervisor-smtp-settings"
            element={
              <PrivateRoute>
                <SupervisorSMTPSettings />
              </PrivateRoute>
            }
          />
          <Route
            path="/activity-logs"
            element={
              <PrivateRoute>
                <ActivityLogs />
              </PrivateRoute>
            }
          />
          <Route
            path="/active-users"
            element={
              <PrivateRoute>
                <ActiveUsers />
              </PrivateRoute>
            }
          />
          <Route
            path="/users"
            element={
              <AdminRoute>
                <UserManagement />
              </AdminRoute>
            }
          />
          <Route
            path="/smtp-settings"
            element={
              <AdminRoute>
                <SMTPSettings />
              </AdminRoute>
            }
          />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </ThemeProvider>
  );
}

export default App;
